export const useLocales = () => {
    const { locale, locales, t, te, rt, tm } = useI18n();

    const days = 60;

    watch(
        () => locale.value,
        () => {
            let expires = '';
            if (days) {
                const date = new Date();
                date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
                expires = '; expires=' + date.toUTCString();
            }
            document.cookie = 'lang=' + `${locale.value}` + expires + '; path=/';
        },
    );

    const matchingLocale = computed(() => {
        for (const localeItem of locales.value) {
            if (typeof localeItem === 'object') {
                if (locale.value === localeItem.code) {
                    return localeItem;
                }
            }
        }
    });

    const isoCode = computed(() => {
        const code = matchingLocale.value?.code.replace(/_/g, '-').toLowerCase();
        return code || 'de-de';
    });

    const isoLocale = computed(() => {
        const iso = matchingLocale.value?.iso;
        return typeof iso === 'string' ? iso : 'de';
    });

    /**
     * ISO 3166-1 alpha-2
     *
     * See: https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
     */
    const iso2Country = computed(() => {
        const country = matchingLocale.value?.countryISO2;
        return (typeof country === 'string' ? country : 'DE').toUpperCase() as Uppercase<string>;
    });

    const regionNamesInLocale = computed(() => new Intl.DisplayNames([isoLocale.value], { type: 'region' }));

    const languageCountrySplit = computed(() => locale.value.toLowerCase().split('_'));
    const language = computed(() => languageCountrySplit.value[0] as string);
    const country = computed(() => languageCountrySplit.value[1]);

    const languageName = computed(() => getLanguageNativeName(language.value));

    const isUS = ref(iso2Country.value.toLowerCase() === 'us');

    const d = (d: string | Date) => {
        const date = typeof d === 'string' ? parseDateTimezoneFix(d) : d;
        return new Intl.DateTimeFormat(isoLocale.value).format(date);
    };
    const n = (num: number) => {
        return new Intl.NumberFormat(isoLocale.value, { maximumFractionDigits: 2 }).format(num);
    };

    const friendlyDate = (value: string, threshold = 30) => {
        const date = parseDateTimezoneFix(value);
        const days = Math.round((date.getTime() - Date.now()) / 864e5);

        if (Math.abs(days) >= threshold) return d(date);
        return new Intl.RelativeTimeFormat(language.value, { numeric: 'auto' }).format(days, 'day');
    };

    const friendlyAge = (age: number | null | undefined, threshold = 1) => {
        if (!age) return '';

        if (age < threshold) {
            return `${yearToMonth(age)} ${t('bikeDetail.month', age)}`;
        }

        return `${n(age)} ${t('bikeDetail.years')}`;
    };
    const friendlyAgeRange = (minAge: number | null | undefined, maxAge: number | null | undefined, threshold = 1) => {
        if (!minAge || !maxAge) return '';

        const formatAgeRange = (min: number, max: number, postFix: string) => `${min} – ${max} ${postFix}`;

        if (minAge < threshold || maxAge < threshold) {
            return formatAgeRange(yearToMonth(minAge), yearToMonth(maxAge), t('bikeDetail.month', 2));
        }

        return `${n(minAge)} – ${n(maxAge)} ${t('bikeDetail.years')}`;
    };

    return {
        /** locale from [vue i18n](https://vue-i18n.intlify.dev/api/composition.html#locale) */
        locale,
        locales,
        /** language in lowercase derived from the current locale */
        language,
        /** country in lowercase derived from the current locale */
        country,
        localeSuffix: locale.value.substring(locale.value.indexOf('_') + 1),
        /** translation function from [vue i18n](https://vue-i18n.intlify.dev/api/composition.html#t) */
        t,
        /** translation exists from [vue i18n](https://vue-i18n.intlify.dev/api/composition.html#te-key-locale) */
        te,
        /** Resolve locale message translation from [vue i18n](https://vue-i18n.intlify.dev/api/composition#rt) */
        rt,
        /** Locale messages getter from [vue i18n](https://vue-i18n.intlify.dev/api/composition#tm-key) */
        tm,
        translateSafe: (translationKey: string, fallback: string) => {
            const translation = t(translationKey);
            return translation === translationKey ? fallback : translation;
        },
        /** date formatting */
        d,
        /** money formatting */
        m: (num: number, currencyCode: string) => {
            return new Intl.NumberFormat(isoLocale.value, {
                style: 'currency',
                currency: currencyCode,
            }).format(num);
        },
        /** number formatting */
        n,
        /** size formatting */
        s: (size: number | [number, number], unitIsImperial: boolean = false) => {
            const formatter = new Intl.NumberFormat(isoLocale.value, {
                style: 'unit',
                unit: unitIsImperial ? 'inch' : 'centimeter',
            });
            if (Array.isArray(size)) {
                return `${n(size[0])} - ${formatter.format(size[1])}`;
            } else {
                return formatter.format(size);
            }
        },
        /** weight formatting */
        w: (weight: string, unitIsImperial: boolean = false) => {
            const parsedWeight = parseFloat(weight);
            return new Intl.NumberFormat(isoLocale.value, {
                style: 'unit',
                unit: unitIsImperial ? 'pound' : 'kilogram',
            }).format(parsedWeight);
        },
        isoCode,
        isoLocale,
        iso2Country,
        languageName,
        regionNamesInLocale,
        isUS,
        flag: 'fi ' + `fi-${iso2Country.value.toLowerCase()}`,
        isInternational: locale.value.toUpperCase().indexOf('_INT') > 1,
        friendlyAge,
        friendlyAgeRange,
        friendlyDate,
    };
};

export const useLocalePathWoom = () => {
    const localePath = useLocalePath();

    return (...props: Parameters<typeof localePath>) => {
        const path = props[0];
        let route = localePath(...props);
        if (path === '/' || (typeof path === 'object' && path.path === '/')) {
            route += '/';
        }
        return route;
    };
};
